import {
    BsMetaUtil,
    LoopscalePointsAction,
    useRewardsPointsBreakdown,
    useTokenListContext
} from "@bridgesplit/abf-react";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { useAppPalette, StatProps, Icon } from "@bridgesplit/ui";
import { formatMultiplier, filterTruthy } from "@bridgesplit/utils";
import { BoltOutlined } from "@mui/icons-material";

import { CaptionWithIcon } from "../points";

export interface UseRewardsPointsBreakdownProps {
    loopscalePointsAction: LoopscalePointsAction | null | undefined;
    identifier: string | undefined;
    collateralMetadata?: TokenListMetadata | undefined;
}

export function useRewardsPointsBreakdownStats({
    loopscalePointsAction,
    identifier,
    collateralMetadata
}: UseRewardsPointsBreakdownProps) {
    const { relativePointsPerSecond, rewardLabel, multiplierInfos, totalMultiplier, isLoading } =
        useRewardsPointsBreakdown({
            loopscalePointsAction,
            identifier,
            collateralMint: collateralMetadata?.mint
        });

    const { prime, secondary } = useAppPalette();
    const { getMetadata } = useTokenListContext();
    const identifierMetadata = getMetadata(identifier);

    // Always show the points stat (shared for all metadatas)
    const pointsStat: StatProps = {
        caption: (
            <CaptionWithIcon
                variant="body2"
                icon={<Icon type="points" />}
                iconColor={secondary}
                caption={rewardLabel ?? "Points"}
            />
        ),
        value: formatMultiplier(relativePointsPerSecond),
        variant: "body2"
    };

    // Build a list of StatProps for boosts
    const boostStats: StatProps[] = multiplierInfos.map((multiplierInfo) => {
        // Determine which metadata to use based on mint
        const isIdentifierMint = multiplierInfo.mint === identifier;
        const isCollateralMint = multiplierInfo.mint === collateralMetadata?.mint;
        const metadata = isIdentifierMint ? identifierMetadata : isCollateralMint ? collateralMetadata : undefined;

        return {
            caption: (
                <CaptionWithIcon
                    icon={<BoltOutlined />}
                    iconColor={prime}
                    variant="body2"
                    caption={`${metadata ? BsMetaUtil.getSymbol(metadata) : "Points"} boost`}
                />
            ),
            value: formatMultiplier(multiplierInfo.multiplier),
            variant: "body2"
        };
    });

    const stats = [pointsStat, ...boostStats].filter(filterTruthy);

    return {
        stats,
        isLoading,
        relativePointsPerSecond,
        rewardLabel,
        totalMultiplier
    };
}
